import { Node, Rect } from 'reactflow';
import {
  SparkButtonEdge,
  SparkStepNode,
  SparkInputOutputValuesNode,
} from '../ChartComponents';
import type { SparkStep } from 'types/SparkStep';
import type {
  NodeRectBound,
  SparkStepBaseNode,
  SparkButtonEdgeType,
  SparkStepNodesType,
} from '../types';
import { numericSort } from 'utilities/sort';
import { SPARK_DRAWER_TYPES } from 'constants/common';

export const NODE_GAP = 260;
export const DISTANCE = NODE_GAP;
export const NODE_MIN_HEIGHT = 60;
export const NODE_MIN_WIDTH = 900;
export const NODES_CONTAINER_MARGIN = 20;
export const NODE_DRAG_THRESHOLD = 9;
export const DURATION_FOR_VIEW = 1000;
export const MIN_ZOOM = 0.5;
export const DEFAULT_ZOOM = 0.8;
export const DUMMY_NODE = 'dummy';
export const PREVIEW_NODE = 'preview';
export const REACT_FLOW_BOUNDS = {
  x: 0,
  y: -20,
  width: NODE_MIN_WIDTH + 80,
  height: 1200,
};

export const sparkBaseEdge = {
  type: 'sparkButtonEdge',
};
export function createSampleSparkButtonEdge() {
  return {
    id: 'sample-spark-button-edge',
    source: 'sparkInputNode',
    data: {
      isPreviewNode: false,
    },
    target: 'sparkOutputNode',
    ...sparkBaseEdge,
  };
}
export const sampleSparkButtonEdge: SparkButtonEdgeType = {
  id: 'sample-spark-button-edge',
  source: 'sparkInputNode',
  data: {
    isPreviewNode: false,
  },
  target: 'sparkOutputNode',
  ...sparkBaseEdge,
};
export const edgeTypes = {
  sparkButtonEdge: SparkButtonEdge,
};
export const nodeTypes = {
  sparkStartNode: SparkInputOutputValuesNode,
  sparkStepBaseNode: SparkStepNode,
  sparkEndNode: SparkInputOutputValuesNode,
};

export function generateRepositionedNodesEdgesByNodes(
  sparkNodesForChart: SparkStepNodesType[],
  updateStepOrder: boolean = false
) {
  const sortedNodesByPosition: SparkStepNodesType[] =
    sortOutNodesByVerticalPosition(sparkNodesForChart, updateStepOrder);
  const sparkEdges: SparkButtonEdgeType[] = sortedNodesByPosition.map(
    (node, index) => {
      const nextNode = sortedNodesByPosition[index + 1];
      const sourceNodeId =
        node.id === 'sparkInputNode' ? 'sparkInputNode' : `${node.id}`;
      const targetNodeId =
        nextNode?.id === 'sparkOutputNode' ||
        index === sortedNodesByPosition?.length - 1
          ? 'sparkOutputNode'
          : `${nextNode.id}`;

      return {
        id: `${node.id}-${nextNode?.id ?? 'sparkOutputNode'}`,
        source: sourceNodeId,
        target: targetNodeId,
        isNextNodeUndefined: nextNode === undefined,
        data: {
          isPreviewNode: false,
        },
        ...sparkBaseEdge,
      };
    }
  );
  const sparkEdgesForChart = sparkEdges.filter(
    (edge) => edge.isNextNodeUndefined !== true
  );
  return { sparkEdgesForChart, sortedNodesByPosition };
}
type ReactFlowDataProp = {
  steps: SparkStep[] | undefined;
  updateStepOrder?: boolean;
  sparkID?: string;
};
export const createReactFlowData = ({
  steps,
  updateStepOrder = false,
  sparkID,
}: ReactFlowDataProp) => {
  const sparkStepBaseNode: SparkStepBaseNode = {
    draggable: true,
    id: 'sparkStepBaseNode',
    data: {},
    position: {
      x: 0,
      y: 0,
    },
    type: 'sparkStepBaseNode',
  };
  const sparkStepNodes: SparkStepNodesType[] | undefined = steps
    ?.filter((spark) => !spark.attributes.isDeleted)
    .sort((a, b) =>
      numericSort(a?.attributes.stepOrder, b?.attributes.stepOrder)
    )
    ?.map((step, index) => {
      return {
        ...sparkStepBaseNode,
        id: `${step?.id}`,
        data: {
          ...sparkStepBaseNode.data,
          stepID: step.id,
          stepOrder: index,
        },
      };
    });
  if (!sparkStepNodes) {
    return;
  }

  const sparkInputOutputNodes: SparkStepNodesType[] = [
    {
      ...sparkStepBaseNode,
      draggable: false,
      id: 'sparkInputNode',
      data: {
        ...sparkStepBaseNode.data,
        count: 0,
        type: SPARK_DRAWER_TYPES.input,
        isFirst: true,
        sparkID: sparkID,
      },
      type: 'sparkStartNode',
    },
    {
      ...sparkStepBaseNode,
      draggable: false,
      id: 'sparkOutputNode',
      data: {
        ...sparkStepBaseNode.data,
        count: 0,
        type: SPARK_DRAWER_TYPES.output,
        isLast: true,
        sparkID: sparkID,
      },
      type: 'sparkEndNode',
    },
  ];

  const sparkNodes: SparkStepNodesType[] = [
    sparkInputOutputNodes[0],
    ...sparkStepNodes,
    sparkInputOutputNodes[1],
  ];

  const sparkNodesForChart: SparkStepNodesType[] = rePositionNodes(sparkNodes);
  if (!sparkNodesForChart) {
    return;
  }
  const { sparkEdgesForChart, sortedNodesByPosition } =
    generateRepositionedNodesEdgesByNodes(sparkNodesForChart, updateStepOrder);

  return {
    nodes: sortedNodesByPosition,
    edges: sparkEdgesForChart,
  };
};
export function isDraggingNodeOutOfBounds(
  node: NodeRectBound,
  nodesBound: Rect,
  margin: number
) {
  if (node.right < nodesBound.x + margin) {
    return true;
  }

  if (node.left > nodesBound.y + nodesBound.width + margin) {
    return true;
  }
  return false;
}
export function getNodeRect(node: Node) {
  return {
    top: node.position.y,
    left: node.position.x,
    bottom: node.position.y + (node.height || NODE_MIN_HEIGHT),
    right: node.position.x + (node.width || NODE_MIN_WIDTH),
    width: node.width || NODE_MIN_WIDTH,
    height: node.height || NODE_MIN_HEIGHT,
  };
}

export function rePositionNodes(nodes: SparkStepNodesType[]) {
  return nodes?.map((node, index: number) => {
    const yPos =
      index === 0
        ? 42
        : index === nodes?.length - 1
          ? (index - 1) * NODE_GAP + 260
          : index * NODE_GAP;

    return {
      ...node,
      position: {
        x: 0,
        y: yPos,
      },
    };
  });
}

export function sortOutNodesByVerticalPosition(
  nodes: Node[],
  updateStepOrder: boolean = false
) {
  const sortedNodes = [...nodes].sort((a, b) => a.position.y - b.position.y);
  if (updateStepOrder) {
    return sortedNodes.map((node, index: number) => {
      return {
        ...node,
        data: {
          ...node?.data,
          stepOrder: index,
        },
      };
    });
  } else return sortedNodes;
}
